import * as React from "react";
import {DetailedHTMLProps, FC, useEffect, useReducer, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {FormInputText} from "../../pages/shop/wholesale/form/FormInputText";
import {FormInputSelectMultiple} from "../../pages/shop/wholesale/form/FormInputSelectMultiple";
import {FormInputSelect} from "../../pages/shop/wholesale/form/FormInputSelect";
import {FilterAlt, Sort} from "@mui/icons-material"; // Add RestartAlt icon
import {Button, CircularProgress, Drawer, Grid, Stack, useMediaQuery} from "@mui/material";
import {FlexFilter, FlexFilterValue, FlexSort} from "./FlexTypes";
import makeStyles from "@mui/styles/makeStyles";
import {TitleText} from "../../layout/Typography";
import FlexCustomizer from "./FlexCustomizer";

export type FlexHeaderData = {
   search: string;
   filterOr: {
      [id: string]: string[]
   };
   filterAnd: {
      [id: string]: string[]
   };
   sort?: string;
   tab?: string;
}

export type FlexHeaderProps = DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
   globalId: string
   count: number | undefined;
   filters?: FlexFilter<any>[];
   sorts?: FlexSort<any>[];
   searchLabel?: string;
   headerData: FlexHeaderData;
   onHeaderChanged: (headerData: FlexHeaderData) => void;
   isSearchLoading?: boolean; // New prop to show if search results are loading
}

// FlexHeader State Management
interface FlexHeaderState {
   filterPanel: boolean;
   localSearchValue: string;
   searchInputChanging: boolean;
   lastSubmittedSearch: string;
   isActiveSearch: boolean;
}

type FlexHeaderAction =
   | { type: 'TOGGLE_FILTER_PANEL'; payload?: boolean }
   | { type: 'SET_LOCAL_SEARCH'; payload: string }
   | { type: 'SET_SEARCH_INPUT_CHANGING'; payload: boolean }
   | { type: 'SET_LAST_SUBMITTED_SEARCH'; payload: string }
   | { type: 'SET_ACTIVE_SEARCH'; payload: boolean }
   | { type: 'SUBMIT_SEARCH'; payload: string }
   | { type: 'RESET_SEARCH'; payload: string };

const initialFlexHeaderState: FlexHeaderState = {
   filterPanel: false,
   localSearchValue: '',
   searchInputChanging: false,
   lastSubmittedSearch: '',
   isActiveSearch: false
};

function flexHeaderReducer(state: FlexHeaderState, action: FlexHeaderAction): FlexHeaderState {
   switch (action.type) {
      case 'TOGGLE_FILTER_PANEL':
         return { ...state, filterPanel: action.payload ?? !state.filterPanel };
      case 'SET_LOCAL_SEARCH':
         return { ...state, localSearchValue: action.payload };
      case 'SET_SEARCH_INPUT_CHANGING':
         return { ...state, searchInputChanging: action.payload };
      case 'SET_LAST_SUBMITTED_SEARCH':
         return { ...state, lastSubmittedSearch: action.payload };
      case 'SET_ACTIVE_SEARCH':
         return { ...state, isActiveSearch: action.payload };
      case 'SUBMIT_SEARCH':
         return {
            ...state,
            lastSubmittedSearch: action.payload,
            isActiveSearch: true,
            searchInputChanging: false
         };
      case 'RESET_SEARCH':
         return {
            ...state,
            localSearchValue: action.payload,
            lastSubmittedSearch: action.payload
         };
      default:
         return state;
   }
}

export const FlexHeader: FC<FlexHeaderProps> = ({
                                                   globalId,
                                                   count,
                                                   filters,
                                                   sorts,
                                                   searchLabel,
                                                   headerData,
                                                   onHeaderChanged,
                                                   isSearchLoading,
                                                   ...props
                                                }) => {
   const [state, dispatch] = useReducer(flexHeaderReducer, {
      ...initialFlexHeaderState,
      localSearchValue: headerData.search || '',
      lastSubmittedSearch: headerData.search || ''
   });

   // Refs to track timers for cleanup
   const searchInputTimerRef = useRef<NodeJS.Timeout>();
   const enterKeyTimerRef = useRef<NodeJS.Timeout>();

   const classes = useStyles();
   const isDesktopLG = useMediaQuery('(min-width:1250px)');
   const isDesktopMD = useMediaQuery('(min-width:800px)');

   const methods = useForm<FlexHeaderData>({
      defaultValues: headerData
   });

   const {watch, getValues, reset, setValue} = methods;

   // Watch the react-hook-form search value for debounce
   const formSearchValue = watch('search');
   const debouncedSearch = useDebounce(formSearchValue, 1800);

   // Cleanup all timers on unmount
   useEffect(() => {
      return () => {
         if (searchInputTimerRef.current) {
            clearTimeout(searchInputTimerRef.current);
         }
         if (enterKeyTimerRef.current) {
            clearTimeout(enterKeyTimerRef.current);
         }
      };
   }, []);

   // Function to check if any filters are applied
   const hasActiveFilters = () => {
      const hasSearch = !!headerData.search;
      const hasSort = !!headerData.sort;
      const hasFilterOr = Object.values(headerData.filterOr || {}).some(values => values?.length > 0);
      const hasFilterAnd = Object.values(headerData.filterAnd || {}).some(values => values?.length > 0);

      return hasSearch || hasSort || hasFilterOr || hasFilterAnd;
   };

   // Initialize local search value when headerData changes (but not during typing or active search)
   useEffect(() => {
      // Only update local search if:
      // 1. We're not currently typing
      // 2. We're not in an active search operation
      // 3. The incoming headerData.search isn't equal to what the user just submitted
      if (!state.searchInputChanging &&
         !state.isActiveSearch &&
         headerData.search !== state.lastSubmittedSearch) {
         dispatch({ type: 'RESET_SEARCH', payload: headerData.search || '' });
      }
   }, [headerData.search, state.searchInputChanging, state.isActiveSearch, state.lastSubmittedSearch]);

   // Update the form when headerData prop changes
   useEffect(() => {
      const currentValues = getValues();

      // Preserve the current search value during form reset
      // If we're actively changing the search input, keep the current value
      // Also keep the current value if headerData.search is empty but we have a value
      // (this protects against unwanted resets)
      const searchToKeep = state.searchInputChanging ||
      (headerData.search === '' && currentValues.search !== '')
         ? currentValues.search
         : headerData.search;

      reset({
         ...headerData,
         search: searchToKeep
      });
   }, [headerData, reset, getValues]); // Removed state.searchInputChanging to prevent re-running when it changes

   // Handle debounced search changes
   useEffect(() => {
      // Skip if:
      // 1. The search hasn't actually changed
      // 2. We're in an active Enter key search operation
      // 3. If it's empty and was just reset
      if (headerData.search === debouncedSearch ||
         state.isActiveSearch ||
         (debouncedSearch === '' && state.localSearchValue !== '')) {
         return;
      }

      // Set tracking variables to prevent flickering during debounced search
      dispatch({ type: 'SUBMIT_SEARCH', payload: debouncedSearch });

      // Only trigger search when user has stopped typing
      onHeaderChanged({
         ...headerData,
         search: debouncedSearch
      });

      // Reset the active search flag after a delay
      const timer = setTimeout(() => {
         dispatch({ type: 'SET_ACTIVE_SEARCH', payload: false });
      }, 2000);

      return () => clearTimeout(timer);
   }, [debouncedSearch, headerData, onHeaderChanged, state.localSearchValue, state.isActiveSearch]);

   // Custom handler for search input changes
   const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      dispatch({ type: 'SET_SEARCH_INPUT_CHANGING', payload: true });

      // Always update local state immediately for responsive UI
      dispatch({ type: 'SET_LOCAL_SEARCH', payload: newValue });
      setValue('search', newValue);

      // Clear any existing timer
      if (searchInputTimerRef.current) {
         clearTimeout(searchInputTimerRef.current);
      }

      // Reset the changing flag after a short delay
      searchInputTimerRef.current = setTimeout(() => {
         dispatch({ type: 'SET_SEARCH_INPUT_CHANGING', payload: false });
      }, 100);
   };

   // New function to handle Enter key press
   const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
         // Prevent form submission default behavior
         e.preventDefault();

         // Immediately trigger search on Enter key
         const currentSearchValue = getValues('search');

         // Set our tracking variables to prevent flickering
         dispatch({ type: 'SUBMIT_SEARCH', payload: currentSearchValue });

         // Always trigger the search to ensure consistency
         onHeaderChanged({
            ...headerData,
            search: currentSearchValue
         });

         // Select the text in the search field
         const inputElement = e.target as HTMLInputElement;
         inputElement.select();

         // Clear any existing timer
         if (enterKeyTimerRef.current) {
            clearTimeout(enterKeyTimerRef.current);
         }

         // Reset the active search flag after a delay
         // This delay needs to be longer than the typical server response time
         enterKeyTimerRef.current = setTimeout(() => {
            dispatch({ type: 'SET_ACTIVE_SEARCH', payload: false });
         }, 2000);
      }
   }

   // Enhanced search input with better indicators and custom change handler
   const renderSearchInput = () => (
      <FormInputText
         name={"search"}
         autoComplete={"off"}
         label={searchLabel || 'Search'}
         value={state.localSearchValue}
         onChange={handleSearchChange}
         onKeyDown={handleSearchKeyDown} // Add keydown handler for Enter key
         InputProps={{
            endAdornment: (
               <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginRight: '0px'
               }}>
                  {isSearchLoading && <CircularProgress size={16} color="inherit"/>}
                  {!isSearchLoading &&
                     <div style={{
                        backgroundColor: '#e0e0e0',
                        marginRight: '-12px',
                        padding: '12px'
                     }}>
                        {count !== undefined && <>{count}</>}
                     </div>}
               </div>
            )
         }}
         onFocus={event => {
            event.target.select();
         }}
         // Prevent form submission on Enter (which might clear the search)
         onSubmit={e => {
            e.preventDefault();
            return false;
         }}
         // Make sure value persists even if parent form resets
         defaultValue={state.localSearchValue}
      />
   );

   const updateHeaders = () => {
      const sortId = getValues('sort');

      const filterAnd = getValues('filterAnd');
      const filterOr = getValues('filterOr');
      const resultFilters: FlexFilterValue[] = [];

      for (const key of Object.keys(filterAnd)) {
         let selected = filterAnd[key];
         if (selected?.length > 0) {
            resultFilters.push({
               filterId: key,
               values: selected,
               requireAll: true
            });
         }
      }

      for (const key of Object.keys(filterOr)) {
         let selected = filterOr[key];
         if (selected?.length > 0) {
            resultFilters.push({
               filterId: key,
               values: selected,
               requireAll: false
            });
         }
      }

      onHeaderChanged({
         ...headerData,
         sort: sortId,
         filterAnd,
         filterOr
      });
   };

   let maxFiltersOnLine = isDesktopLG ? 2 : isDesktopMD ? 1 : 0;
   let sortSize = isDesktopLG ? 20 : isDesktopMD ? 25 : 50;
   let filterSize = isDesktopLG ? 20 : isDesktopMD ? 30 : 10;
   let filterPanelWidth: number = 100 - (Math.min(maxFiltersOnLine, (filters || []).length) * filterSize) - ((sorts || []).length > 0 ? sortSize : 0);

   // Adjust width if reset button is visible
   if (hasActiveFilters()) {
      filterPanelWidth -= 10; // Reserve space for reset button
   }

   return <div {...props}>
      <FormProvider {...methods}>
         <Drawer variant={"persistent"} open={state.filterPanel} anchor={"right"} onClose={() => dispatch({ type: 'TOGGLE_FILTER_PANEL', payload: false })}
                 BackdropProps={{invisible: true}}>
            <Stack direction={"column"} spacing={1} className={classes.filterDrawer}>
               <TitleText type={"h2"}>More filters</TitleText>
               {filters?.map(filter => {
                  return <Grid item xs key={'flex_filter_' + filter.id}>
                     <FormInputSelectMultiple
                        icon={<FilterAlt/>}
                        name={'filterOr.' + filter.id}
                        label={filter.label}
                        onChange={() => updateHeaders()}
                        options={
                           filter?.options?.map(option => {
                              return {
                                 id: option.id || option.label,
                                 label: option.label,
                                 subtext: option.subtext
                              }
                           }) || []
                        }/>
                  </Grid>
               })}
               <Button variant={"contained"} color={"primary"} onClick={() => dispatch({ type: 'TOGGLE_FILTER_PANEL', payload: false })}>
                  Done
               </Button>
            </Stack>
         </Drawer>
         <Stack direction={"row"} spacing={1}>
            <div style={{width: filterPanelWidth + '%'}}>
               {renderSearchInput()}
            </div>
            <div style={{width: (100 - filterPanelWidth) + '%'}}>
               <Stack direction={"row"} spacing={1}>
                  {filters?.slice(0, maxFiltersOnLine).map(filter => {
                     return <Grid item xs key={'flex_filter_' + filter.id}>
                        <FormInputSelectMultiple
                           icon={<FilterAlt/>}
                           name={'filterOr.' + filter.id}
                           label={filter.label}
                           onChange={() => updateHeaders()}
                           options={
                              filter?.options?.map(option => {
                                 return {
                                    id: option.id || option.label,
                                    label: option.label,
                                    subtext: option.subtext
                                 }
                              }) || []
                           }/>
                     </Grid>
                  })}
                  {(filters || []).length > maxFiltersOnLine && <div className={classes.button}>
                     <Button
                        color={"secondary"}
                        size={"small"}
                        variant={"outlined"}
                        style={{borderColor: '#d0d0d0'}}
                        onClick={() => dispatch({ type: 'TOGGLE_FILTER_PANEL', payload: true })}>
                        Filters
                     </Button>
                  </div>}

                  {(sorts || []).length > 0 && <Grid item xs>
                     <FormInputSelect
                        icon={<Sort/>}
                        name={"sort"}
                        label={"Sort"}
                        onSelectChange={() => updateHeaders()}
                        options={sorts!.map(sort => {
                           return {
                              id: sort.id,
                              name: sort.label
                           }
                        })}/>
                  </Grid>}
                  <FlexCustomizer globalId={globalId}/>
               </Stack>
            </div>
         </Stack>
      </FormProvider>
   </div>
}

function useDebounce<TValue>(value: TValue, delay: number = 300) {
   const [debouncedValue, setDebouncedValue] = useState(value)

   useEffect(() => {
      const handler = setTimeout(() => {
         setDebouncedValue(value)
      }, delay)

      return () => {
         clearTimeout(handler)
      }
   }, [value, delay])

   return debouncedValue
}

const useStyles = makeStyles({
   filterDrawer: {
      minWidth: '500px',
      padding: '20px'
   },
   button: {
      marginBottom: '8px !important',
      "& button": {
         height: '100%'
      }
   },
   resetButton: {
      borderColor: '#d0d0d0',
      '&:hover': {
         backgroundColor: '#f8f8f8'
      }
   }
});

